import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Calendar, Clock, MapPin, Users, Check, CreditCard, FileText, ChevronDown, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input, TextArea } from '../../components/ui/Input';
import { DecorativeShape } from '../../components/DecorativeShape';
import { Animate } from '../../components/ui/Animate';
import { useLanguage } from '../../context/LanguageContext';

/* ------------------------------------------------------------------
   STILLINGAR - allt sem þarf að fylla út áður en síðan fer í loftið
   ------------------------------------------------------------------ */
const CONFIG = {
  // true = gulur "DRÖG" borði efst á síðunni. Setja á false þegar allt er klárt.
  draft: true,
  // Skráningar sendar í tölvupósti með FormSubmit - sömu þjónustu og á tengiliða- og viðburðasíðunni.
  // Tómt = skráningar eru ekki sendar neitt (aðeins prófun).
  formEndpoint: 'https://formsubmit.co/ajax/helgi@helgig.com',
  courseName: 'Betri samskipti og heilbrigðari ágreiningar',
  location: 'Reykjavík - nákvæm staðsetning kynnt síðar',
  maxParticipants: 14,
  cancellationPolicy: 'Full endurgreiðsla ef afbókað er minnst 7 dögum fyrir námskeiðið. Eftir það er velkomið að senda annan þátttakanda í þinn stað. Ef lágmarksfjöldi næst ekki fellur námskeiðið niður og þú færð fulla endurgreiðslu.',
  options: [{
    id: 'half-am',
    enabled: true,
    name: 'Hálfur dagur - fyrir hádegi',
    date: 'Fimmtudagur 12. nóvember 2026',
    time: '9:00 - 12:30',
    price: 39900,
    // Greiðslutengill frá greiðsluþjónustu (t.d. Adyen Pay by Link)
    paymentLink: ''
  }, {
    id: 'half-pm',
    enabled: true,
    name: 'Hálfur dagur - eftir hádegi',
    date: 'Fimmtudagur 12. nóvember 2026',
    time: '13:00 - 16:30',
    price: 39900,
    paymentLink: ''
  }, {
    id: 'full',
    enabled: true,
    name: 'Heill dagur',
    date: 'Fimmtudagur 19. nóvember 2026',
    time: '9:00 - 16:00',
    price: 69900,
    paymentLink: ''
  }]
};
type CourseOption = (typeof CONFIG.options)[number];
type PaymentMethod = 'card' | 'invoice';
function formatISK(n: number) {
  return n.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ' kr.';
}
const outcomes = ['Skýran ramma til að greina hvað er ágreiningur - og hvað ekki', 'Betri skilning á eigin viðbrögðum og því sem kveikir í þér', 'Umferðarljósin: einfalt líkan til að meta hversu langt ágreiningur er genginn', 'Hagnýtar aðferðir sem þú æfir á námskeiðinu og prófar strax í vikunni á eftir'];
const audience = ['Stjórnendur og teymisstjórar', 'Mannauðsfólk sem styður teymi og stjórnendur', 'Allir sem vilja takast á við krefjandi samskipti af meira öryggi'];
const halfDayAgenda = [{
  t: '0:00',
  d: 'Móttaka og kynning - hvaða aðstæður komu með þér?'
}, {
  t: '0:20',
  d: 'Samskiptaskalinn: hvað er ágreiningur og hvað ekki'
}, {
  t: '0:55',
  d: 'Þín viðbrögð: mynstur, kveikjur og sjálfsstjórn (æfing)'
}, {
  t: '1:30',
  d: 'Hlé'
}, {
  t: '1:45',
  d: 'Umferðarljósin: hvernig ágreiningar stigmagnast'
}, {
  t: '2:20',
  d: 'Verkfærakistan: aðferðir æfðar í pörum'
}, {
  t: '3:10',
  d: 'Samantekt: eitt skref sem þú prófar í næstu viku'
}];
const fullDayAgenda = [{
  t: '9:00',
  d: 'Móttaka og kynning - hvaða aðstæður komu með þér?'
}, {
  t: '9:20',
  d: 'Samskiptaskalinn og ólíkar tegundir ágreininga'
}, {
  t: '10:15',
  d: 'Hlé'
}, {
  t: '10:30',
  d: 'Þín viðbrögð: mynstur, kveikjur og sjálfsstjórn (æfingar)'
}, {
  t: '12:00',
  d: 'Hádegismatur'
}, {
  t: '12:45',
  d: 'Stigmögnun: umferðarljósin og líkan Glasl'
}, {
  t: '13:45',
  d: 'Hlé'
}, {
  t: '14:00',
  d: 'Verkfærakistan: æfingar á raunverulegum aðstæðum þátttakenda'
}, {
  t: '15:30',
  d: 'Samantekt og næstu skref'
}];
const faqs = [{
  q: 'Á hvaða tungumáli fer námskeiðið fram?',
  a: 'Á íslensku.'
}, {
  q: 'Hversu margir verða í hópnum?',
  a: `Hámark ${CONFIG.maxParticipants} þátttakendur, svo allir fái tíma til að æfa sig og spyrja.`
}, {
  q: 'Þarf ég að undirbúa mig?',
  a: 'Nei. Gott er að hafa í huga eina aðstæðu úr eigin starfi sem þú vilt skilja betur - en þú ræður hversu mikið þú deilir.'
}, {
  q: 'Er farið með það sem ég segi sem trúnaðarmál?',
  a: 'Já. Það sem sagt er á námskeiðinu fer ekki lengra, og allar æfingar má gera með tilbúnum dæmum ef þú kýst það.'
}, {
  q: 'Get ég fengið styrk frá stéttarfélagi?',
  a: 'Margir starfsmenntasjóðir og stéttarfélög styrkja námskeið af þessu tagi. Þú færð kvittun eða reikning sem þú getur notað til að sækja um. Kannaðu rétt þinn hjá þínu stéttarfélagi.'
}, {
  q: 'Hvað ef ég þarf að afbóka?',
  a: CONFIG.cancellationPolicy
}, {
  q: 'Hvað gerist eftir námskeiðið?',
  a: 'Þú færð samantekt með helstu líkönum og aðferðum. Nokkrum vikum síðar kemur stutt eftirfylgni: hvað hefur reynst vel og hvað ekki.'
}, {
  q: 'Getum við fengið námskeiðið inn á okkar vinnustað?',
  a: 'Já. Námskeiðið er einnig í boði innanhúss, aðlagað að aðstæðum ykkar. Hafðu samband og við finnum út hvað hentar.'
}];
export function ConflictCourseSignup() {
  const {
    language
  } = useLanguage();
  const enabledOptions = CONFIG.options.filter(o => o.enabled);
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [optionId, setOptionId] = useState<string>('');
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    workplace: '',
    role: '',
    participants: '1',
    otherNames: '',
    hopes: ''
  });
  const [payment, setPayment] = useState<PaymentMethod>('card');
  const [invoice, setInvoice] = useState({
    company: '',
    kennitala: '',
    invoiceEmail: '',
    reference: ''
  });
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const selected: CourseOption | undefined = enabledOptions.find(o => o.id === optionId);
  const count = Math.max(1, parseInt(form.participants || '1', 10) || 1);
  const total = selected ? selected.price * count : 0;
  const cardAllowed = count === 1;
  const effectivePayment: PaymentMethod = cardAllowed ? payment : 'invoice';
  const upd = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setForm({
    ...form,
    [k]: e.target.value
  });
  const updInv = (k: keyof typeof invoice) => (e: React.ChangeEvent<HTMLInputElement>) => setInvoice({
    ...invoice,
    [k]: e.target.value
  });
  const scrollToSignup = () => {
    document.getElementById('skraning')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  const validateStep2 = () => {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Vinsamlegast skráðu nafn';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = 'Vinsamlegast skráðu gilt netfang';
    if (!form.workplace.trim()) e.workplace = 'Vinsamlegast skráðu vinnustað';
    if (count > 1 && !form.otherNames.trim()) e.otherNames = 'Vinsamlegast skráðu nöfn og netföng annarra þátttakenda';
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const validateStep3 = () => {
    const e: Record<string, string> = {};
    if (effectivePayment === 'invoice') {
      if (!invoice.company.trim()) e.company = 'Vinsamlegast skráðu heiti greiðanda';
      if (!/^\d{6}-?\d{4}$/.test(invoice.kennitala.trim())) e.kennitala = 'Kennitala á að vera 10 tölustafir';
      if (!/^\S+@\S+\.\S+$/.test(invoice.invoiceEmail.trim())) e.invoiceEmail = 'Vinsamlegast skráðu gilt netfang';
    }
    if (!agree) e.agree = 'Vinsamlegast staðfestu skilmálana';
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  const submit = async () => {
    if (!selected || !validateStep3()) return;
    if (effectivePayment === 'card' && !selected.paymentLink) {
      setSubmitError('Kortagreiðsla er ekki orðin virk. Veldu að fá reikning, eða reyndu aftur síðar.');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    const payload = {
      course: CONFIG.courseName,
      option: selected.name,
      date: selected.date,
      time: selected.time,
      unitPrice: selected.price,
      participants: count,
      total,
      payment: effectivePayment === 'card' ? 'Kort' : 'Reikningur',
      ...form,
      ...(effectivePayment === 'invoice' ? invoice : {}),
      submittedAt: new Date().toISOString(),
      _subject: `Skráning: ${CONFIG.courseName} - ${selected.name} (${effectivePayment === 'card' ? 'kort' : 'reikningur'})`
    };
    try {
      if (CONFIG.formEndpoint) {
        const res = await fetch(CONFIG.formEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!res.ok) throw new Error('form');
      } else {
        console.info('Skráning (prófun, ekkert sent):', payload);
      }
      if (effectivePayment === 'card') {
        window.location.href = selected.paymentLink;
        return;
      }
      setStep(4);
    } catch {
      setSubmitError('Eitthvað fór úrskeiðis við skráninguna. Reyndu aftur, eða sendu póst beint á helgi@helgig.com.');
    } finally {
      setSubmitting(false);
    }
  };
  const stepLabels = ['Tímasetning', 'Upplýsingar', 'Greiðsla'];
  return <PageTransition data-id="element-767" data-mp="13tcn5kvg0czn">
      {CONFIG.draft && <div className="bg-yellow-100 border-b border-yellow-300 text-yellow-900 text-sm py-2 px-4 text-center flex items-center justify-center gap-2" data-id="element-768" data-mp="18mxonmw8vp3l">
          <AlertTriangle size={16} data-id="element-769" data-mp="b4ntg6wpv9xx" /> DRÖG - dagsetningar, verð og greiðslutenglar eru ekki staðfest
        </div>}
      <div className="pt-12 pb-24" data-id="element-770" data-mp="wjnt9gp6kfle">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-6" data-id="element-771" data-mp="wim8ulm9ygr0">
          <Link to="/events" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-coral" data-id="element-772" data-mp="11hz33rfh6q3m"><ArrowLeft size={14} data-id="element-773" data-mp="16hbsaittk99d" /> Viðburðir</Link>
        </div>
        {language === 'en' && <div className="max-w-3xl mx-auto px-4 mb-8" data-id="element-774" data-mp="1bh7tybf1nolr">
            <Card className="p-5 bg-teal/5 border border-teal/20 text-sm text-gray-700" data-id="element-775" data-mp="1belnyg7sxh5n">
              This course is taught in Icelandic. For an English-language or in-house version,{' '}
              <Link to="/contact" className="text-coral font-semibold hover:underline" data-id="element-776" data-mp="isaeeg27w5wp">get in touch</Link>.
            </Card>
          </div>}

        {/* HERO */}
        <section className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20 text-center" data-id="element-777" data-mp="sivj8l8hi6o2">
          <DecorativeShape type="blob" color="bg-coral/10" className="top-0 right-0 w-[400px] h-[400px] translate-x-1/4 -translate-y-1/4 -z-10" data-id="element-778" data-mp="3r6rh589h9nwd" />
          <DecorativeShape type="circle" color="bg-teal/10" className="bottom-0 left-0 w-64 h-64 -translate-x-1/4 translate-y-1/4 -z-10" data-id="element-779" data-mp="3w4kkltjec3sy" />
          <Animate data-id="element-780" data-mp="299xu22a39lel">
            <span className="inline-block px-4 py-2 bg-coral/10 text-coral font-medium rounded-full text-sm mb-6" data-id="element-781" data-mp="29bimnmcueu9t">
              Opið námskeið - haust 2026
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight" data-id="element-782" data-mp="1zct4dsh1b7t1">
              Betri samskipti og <span className="text-coral" data-id="element-783" data-mp="1ud6plelk5ji4">heilbrigðari ágreiningar</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8 leading-relaxed" data-id="element-784" data-mp="1ploq6uz6nqz9">
              Flest námskeið um ágreining ganga út frá því að báðir aðilar vilji leysa málið. Í raunveruleikanum er það sjaldnast svo einfalt. Hér lærir þú að lesa í það sem er að gerast, ná betri tökum á eigin viðbrögðum og ganga inn í krefjandi samskipti með skýrari hugsun - líka þegar hinn aðilinn sér ekki vandann.
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-sm font-medium text-gray-600 mb-10" data-id="element-785" data-mp="16odu1j8uldo1">
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-786" data-mp="wyysxmjyqmhm"><Clock size={16} className="text-coral" data-id="element-787" data-mp="120ed7cebgoyy" /><span data-id="element-788" data-mp="nz1yf4aw6ten">Hálfur dagur eða heill dagur</span></div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-789" data-mp="o0mskstaoabz"><MapPin size={16} className="text-coral" data-id="element-790" data-mp="s0x9v1mjen65" /><span data-id="element-791" data-mp="1fqhv5r65xwil">{CONFIG.location}</span></div>
              <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100" data-id="element-792" data-mp="1fqrjzbfh1xmp"><Users size={16} className="text-coral" data-id="element-793" data-mp="1kojaq24drdpb" /><span data-id="element-794" data-mp="16y61lwfr5sye">Hámark {CONFIG.maxParticipants} þátttakendur</span></div>
            </div>
            <Button size="lg" rightIcon={<ArrowRight size={20} data-id="element-796" data-mp="1ayq3fdb9tkqv" />} onClick={scrollToSignup} data-id="element-795" data-mp="1az9j0ndg7x1q">Skrá mig</Button>
          </Animate>
        </section>

        {/* OUTCOMES + AUDIENCE */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-797" data-mp="1yn98ljz0xheq">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10" data-id="element-798" data-mp="23h3osdff7e92">
            <Animate data-id="element-799" data-mp="3o5fyvkoijf07">
              <h2 className="text-2xl font-bold text-charcoal mb-5" data-id="element-800" data-mp="3o4mw0usod9nc">Það sem þú tekur með þér</h2>
              <div className="space-y-3" data-id="element-801" data-mp="3j6lfagiq1mf1">
                {outcomes.map((o, i) => <div key={i} className="flex items-start gap-3" data-id="element-802" data-mp="1plgpo63es2m" data-mpi={i}>
                    <div className="mt-1 p-1 rounded-full bg-coral/10 text-coral flex-shrink-0" data-id="element-803" data-mp="3sx7l5j0myykj"><Check size={13} strokeWidth={3} data-id="element-804" data-mp="3szadi9p9h3sf" /></div>
                    <span className="text-gray-700 leading-relaxed" data-id="element-805" data-mp="3584xnujbmbi4">{o}</span>
                  </div>)}
              </div>
            </Animate>
            <Animate delay={0.1} data-id="element-806" data-mp="3f59o46k78t4q">
              <h2 className="text-2xl font-bold text-charcoal mb-5" data-id="element-807" data-mp="3f6aye8te5ble">Fyrir hvern?</h2>
              <div className="space-y-3 mb-6" data-id="element-808" data-mp="3a66oj1mznf45">
                {audience.map((o, i) => <div key={i} className="flex items-start gap-3" data-id="element-809" data-mp="ueiwnkmu0cs7" data-mpi={i}>
                    <div className="mt-1 p-1 rounded-full bg-teal/10 text-teal flex-shrink-0" data-id="element-810" data-mp="pg7mf8smk09g"><Check size={13} strokeWidth={3} data-id="element-811" data-mp="pfxwfl7kxt3r" /></div>
                    <span className="text-gray-700 leading-relaxed" data-id="element-812" data-mp="2v59p4z9yxnzx">{o}</span>
                  </div>)}
              </div>
              <p className="text-gray-600 leading-relaxed text-sm border-l-4 border-teal/30 pl-4" data-id="element-813" data-mp="303un0s1qzvl7">
                Námskeiðið er verklegt og gagnvirkt: stuttar innlagnir, mikið af æfingum og samtölum í litlum hópum. Þú vinnur með raunverulegar aðstæður - en deilir aðeins því sem þú kýst að deila.
              </p>
            </Animate>
          </div>
        </section>

        {/* OPTIONS + AGENDA */}
        <section className="bg-gray-50 py-16 mb-20" data-id="element-814" data-mp="3j0m0ulyitihv">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-815" data-mp="3nsx4nzwdtofp">
            <Animate className="mb-8" data-id="element-816" data-mp="3rur09trq0290">
              <h2 className="text-2xl font-bold text-charcoal" data-id="element-817" data-mp="3rtpeovd2vxd1">Tvær útfærslur</h2>
              <p className="text-gray-500 mt-1" data-id="element-818" data-mp="ozboytlcxob">Sama efni og sama nálgun. Heill dagur gefur meiri tíma til æfinga og til að vinna með eigin aðstæður.</p>
            </Animate>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-819" data-mp="frh9uawoaa2j">
              <Animate data-id="element-820" data-mp="fssd89m7hft4">
                <Card className="p-7 h-full border-2 border-gray-100 bg-white" data-id="element-821" data-mp="fp4m1h48h5hk">
                  <div className="flex items-center justify-between mb-2" data-id="element-822" data-mp="auh4613qj9by">
                    <h3 className="font-bold text-xl text-charcoal" data-id="element-823" data-mp="5u2vcgub7vcv">Hálfur dagur</h3>
                    <span className="text-coral font-bold" data-id="element-824" data-mp="1srep0teykgt">{formatISK(CONFIG.options[0].price)}</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-5" data-id="element-825" data-mp="yhgrwdu3iowg">3,5 klst. - fyrir eða eftir hádegi. Kaffi og léttar veitingar innifalin.</p>
                  <div className="space-y-2" data-id="element-826" data-mp="os5on7tw4egh">
                    {halfDayAgenda.map((a, i) => <div key={i} className="flex gap-3 text-sm" data-id="element-827" data-mp="kqk4d1f10xw9" data-mpi={i}>
                        <span className="w-12 flex-shrink-0 font-medium text-gray-400" data-id="element-828" data-mp="kqbtff2gs2np">{a.t}</span>
                        <span className={a.d === 'Hlé' ? 'text-gray-400 italic' : 'text-gray-700'} data-id="element-829" data-mp="1h9rib87kcudo">{a.d}</span>
                      </div>)}
                  </div>
                  <p className="text-xs text-gray-400 mt-4" data-id="element-830" data-mp="36q3uoek7804k">Tímar sýndir frá upphafi námskeiðs.</p>
                </Card>
              </Animate>
              <Animate delay={0.08} data-id="element-831" data-mp="2wzsd3p7ldsf7">
                <Card className="p-7 h-full border-2 border-coral/30 bg-white" data-id="element-832" data-mp="2wyr3zqa4zg04">
                  <div className="flex items-center justify-between mb-2" data-id="element-833" data-mp="31yddbomugc3n">
                    <h3 className="font-bold text-xl text-charcoal" data-id="element-834" data-mp="2nz3z2ui77s2b">Heill dagur</h3>
                    <span className="text-coral font-bold" data-id="element-835" data-mp="2o16whzloknh2">{formatISK(CONFIG.options[2].price)}</span>
                  </div>
                  <p className="text-sm text-gray-500 mb-5" data-id="element-836" data-mp="2e16dyfvmybdx">9:00 - 16:00. Hádegismatur, kaffi og léttar veitingar innifalin.</p>
                  <div className="space-y-2" data-id="element-837" data-mp="2j12hke0f80sy">
                    {fullDayAgenda.map((a, i) => <div key={i} className="flex gap-3 text-sm" data-id="element-838" data-mp="caml97bebl7e" data-mpi={i}>
                        <span className="w-12 flex-shrink-0 font-medium text-gray-400" data-id="element-839" data-mp="h8eblwwe6z9h">{a.t}</span>
                        <span className={a.d === 'Hlé' || a.d === 'Hádegismatur' ? 'text-gray-400 italic' : 'text-gray-700'} data-id="element-840" data-mp="21qfdcqawrqh3">{a.d}</span>
                      </div>)}
                  </div>
                </Card>
              </Animate>
            </div>
          </div>
        </section>

        {/* TRAINER */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-841" data-mp="26oqmszxajyys">
          <Animate data-id="element-842" data-mp="2kgwuryqaizn5">
            <h2 className="text-2xl font-bold text-charcoal mb-5" data-id="element-843" data-mp="2kg56l8z0w46w">Leiðbeinandi</h2>
            <p className="text-gray-600 leading-relaxed mb-4" data-id="element-844" data-mp="2gg4j6yty4inp">
              Helgi Guðmundsson er menntaður í vinnu- og skipulagssálfræði frá Gautaborgarháskóla, ICF-vottaður markþjálfi (ACC) og með sérþjálfun í lausn ágreininga og sáttamiðlun. Hann hefur starfað sem markþjálfi á alþjóðavísu hjá Egencia (Expedia Group), kennt við IHM Business School í Gautaborg og er meðhöfundur tveggja kafla í bókinni <em data-id="element-845" data-mp="v2i9joehduwe">Agile People Principles</em>.
            </p>
            <p className="text-gray-600 leading-relaxed" data-id="element-846" data-mp="v4cu31lubkzy">
              Hann hefur skrifað ítarlega um ágreining, endurgjöf og samskipti á vinnustöðum - <Link to="/writing/on-conflict" className="text-coral font-semibold hover:underline" data-id="element-847" data-mp="e7nrz4nt7d5o">sjá greinaröðina On Conflict</Link>.
            </p>
          </Animate>
        </section>

        {/* SIGNUP */}
        <section id="skraning" className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-20 scroll-mt-24" data-id="element-848" data-mp="ny8q6qzkmfo2">
          <Animate data-id="element-849" data-mp="3mge6w1hfvtg3">
            <Card className="p-8 md:p-10 border-t-4 border-coral" data-id="element-850" data-mp="3mh5uap0vukva">
              <h2 className="text-3xl font-bold text-charcoal mb-2" data-id="element-851" data-mp="3re63h8t143qm">Skráning</h2>
              {step < 4 && <div className="flex items-center gap-2 mb-8 text-sm" data-id="element-852" data-mp="7d0bzeodujg">
                  {stepLabels.map((l, i) => <React.Fragment key={l} data-id="element-853" data-mp="592evp13wqca" data-mpi={i}>
                      <div className={`flex items-center gap-2 ${step >= i + 1 ? 'text-coral font-bold' : 'text-gray-400'}`} data-id="element-854" data-mp="1bntl889013dp">
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${step > i + 1 ? 'bg-coral text-white' : step === i + 1 ? 'border-2 border-coral' : 'border-2 border-gray-200'}`} data-id="element-855" data-mp="1glv0ei3b0ijw">
                          {step > i + 1 ? <Check size={14} data-id="element-856" data-mp="2w6dt7l3syuji" /> : i + 1}
                        </span>
                        <span className="hidden sm:inline" data-id="element-857" data-mp="2rdayvar7bh29">{l}</span>
                      </div>
                      {i < 2 && <div className="flex-1 h-0.5 bg-gray-200" data-id="element-858" data-mp="2mf9emmi1hbp7" />}
                    </React.Fragment>)}
                </div>}

              {/* STEP 1 */}
              {step === 1 && <div data-id="element-859" data-mp="2dhh03kk5682v">
                  <p className="font-medium text-charcoal mb-4" data-id="element-860" data-mp="28frl5ttsta9i">Veldu tímasetningu</p>
                  <div className="space-y-3 mb-8" data-id="element-861" data-mp="23gwu64logfeb">
                    {enabledOptions.map(o => <label key={o.id} className={`flex items-start gap-4 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${optionId === o.id ? 'border-coral bg-coral/5' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-862" data-mp="6omv327ikhra">
                        <input type="radio" name="option" className="mt-1 accent-coral" checked={optionId === o.id} onChange={() => setOptionId(o.id)} data-id="element-863" data-mp="tjwbchwtd68i" />
                        <div className="flex-1" data-id="element-864" data-mp="omml501loqjm">
                          <div className="flex items-center justify-between gap-4" data-id="element-865" data-mp="jt1q9gvb0con">
                            <span className="font-bold text-charcoal" data-id="element-866" data-mp="ev9xylqqyojx">{o.name}</span>
                            <span className="font-bold text-coral whitespace-nowrap" data-id="element-867" data-mp="asvdjx7fjgqj">{formatISK(o.price)}</span>
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-gray-500 mt-1" data-id="element-868" data-mp="5t913pzfe4is">
                            <span className="flex items-center gap-1" data-id="element-869" data-mp="w0ijoad7yh0"><Calendar size={14} data-id="element-870" data-mp="wscyvs87q5p" />{o.date}</span>
                            <span className="flex items-center gap-1" data-id="element-871" data-mp="3s2jxnfn8ial4"><Clock size={14} data-id="element-872" data-mp="3s3mmli0zy2ye" />{o.time}</span>
                          </div>
                        </div>
                      </label>)}
                  </div>
                  <div className="flex justify-end" data-id="element-873" data-mp="1q82ndpw2f0wt">
                    <Button disabled={!optionId} rightIcon={<ArrowRight size={18} data-id="element-875" data-mp="1yp37wlun9yfu" />} onClick={() => setStep(2)} data-id="element-874" data-mp="1yq4jqsjhioym">Áfram</Button>
                  </div>
                </div>}

              {/* STEP 2 */}
              {step === 2 && <div data-id="element-876" data-mp="2ciazwemn0vy2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5" data-id="element-877" data-mp="2ciu4yur6vpuo">
                    <Input label="Nafn *" name="name" value={form.name} onChange={upd('name')} error={errors.name} data-id="element-878" data-mp="1fs1r83rqa9w2" />
                    <Input label="Netfang *" name="email" type="email" value={form.email} onChange={upd('email')} error={errors.email} data-id="element-879" data-mp="1jt3s0p1amu05" />
                    <Input label="Sími" name="phone" type="tel" value={form.phone} onChange={upd('phone')} data-id="element-880" data-mp="1or59j5j2ml9i" />
                    <Input label="Vinnustaður *" name="workplace" value={form.workplace} onChange={upd('workplace')} error={errors.workplace} data-id="element-881" data-mp="1tt4cwfet9i6g" />
                    <Input label="Starfsheiti" name="role" value={form.role} onChange={upd('role')} data-id="element-882" data-mp="356q220ko7jrz" />
                    <Input label="Fjöldi þátttakenda" name="participants" type="number" min={1} max={CONFIG.maxParticipants} value={form.participants} onChange={upd('participants')} data-id="element-883" data-mp="2fib6a77ahalp" />
                  </div>
                  {count > 1 && <div className="mb-5" data-id="element-884" data-mp="2pgi9zockhw5r">
                      <TextArea label="Nöfn og netföng annarra þátttakenda *" name="otherNames" rows={3} value={form.otherNames} onChange={upd('otherNames')} error={errors.otherNames} data-id="element-885" data-mp="2khxgs8vqgcqt" />
                    </div>}
                  <div className="mb-8" data-id="element-886" data-mp="1rub4mc8fvzsc">
                    <TextArea label="Hvað vonast þú til að fá út úr námskeiðinu? (valfrjálst)" name="hopes" rows={3} value={form.hopes} onChange={upd('hopes')} data-id="element-887" data-mp="1rr5fwy3q2zx5" />
                  </div>
                  <div className="flex justify-between" data-id="element-888" data-mp="21puvulc26aar">
                    <Button variant="ghost" leftIcon={<ArrowLeft size={18} data-id="element-890" data-mp="1e0ado4nqbh2j" />} onClick={() => setStep(1)} data-id="element-889" data-mp="1e1btemfowrql">Til baka</Button>
                    <Button rightIcon={<ArrowRight size={18} data-id="element-892" data-mp="192iaufljqajk" />} onClick={() => validateStep2() && setStep(3)} data-id="element-891" data-mp="191z3ttxfp6kb">Áfram</Button>
                  </div>
                </div>}

              {/* STEP 3 */}
              {step === 3 && selected && <div data-id="element-893" data-mp="3as7p4q5d06zf">
                  <div className="bg-gray-50 rounded-2xl p-5 mb-6 text-sm" data-id="element-894" data-mp="3aspkzlo40j6t">
                    <div className="flex justify-between mb-1" data-id="element-895" data-mp="3fqrbusrs1msu"><span className="text-gray-500" data-id="element-896" data-mp="3frsqf7804rfb">Námskeið</span><span className="font-medium text-charcoal text-right" data-id="element-897" data-mp="7b7mi4u88oj2">{selected.name}</span></div>
                    <div className="flex justify-between mb-1" data-id="element-898" data-mp="caalb7jm5yil"><span className="text-gray-500" data-id="element-899" data-mp="c8zf712p4eob">Tími</span><span className="text-charcoal text-right" data-id="element-900" data-mp="c7y1eouape2w">{selected.date}, {selected.time}</span></div>
                    <div className="flex justify-between mb-1" data-id="element-901" data-mp="2c4huli5skbm"><span className="text-gray-500" data-id="element-902" data-mp="2b37ykcvq3vh">Þátttakendur</span><span className="text-charcoal" data-id="element-903" data-mp="2dz546gwkoiv">{count} x {formatISK(selected.price)}</span></div>
                    <div className="flex justify-between pt-3 mt-3 border-t border-gray-200 text-base" data-id="element-904" data-mp="2n2de8e4losht"><span className="font-bold text-charcoal" data-id="element-905" data-mp="2n08z10194728">Samtals</span><span className="font-bold text-coral" data-id="element-906" data-mp="339rrhk7dv45p">{formatISK(total)}</span></div>
                  </div>

                  <p className="font-medium text-charcoal mb-3" data-id="element-907" data-mp="2yhzwmxh72c9r">Greiðslumáti</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-id="element-908" data-mp="2tix2w96nzsh3">
                    <label className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-colors ${!cardAllowed ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} ${effectivePayment === 'card' ? 'border-coral bg-coral/5' : 'border-gray-200'}`} data-id="element-909" data-mp="2okvjfn5btr53">
                      <input type="radio" name="payment" className="accent-coral" disabled={!cardAllowed} checked={effectivePayment === 'card'} onChange={() => setPayment('card')} data-id="element-910" data-mp="3ha2e2b1ntwl2" />
                      <CreditCard size={20} className="text-coral" data-id="element-911" data-mp="387t9e1t2oxfl" />
                      <span className="font-medium text-charcoal" data-id="element-912" data-mp="20vj2u27o6gsr">Greiða með korti</span>
                    </label>
                    <label className={`flex items-center gap-3 p-4 rounded-2xl border-2 cursor-pointer transition-colors ${effectivePayment === 'invoice' ? 'border-coral bg-coral/5' : 'border-gray-200'}`} data-id="element-913" data-mp="1wscver8tmjkp">
                      <input type="radio" name="payment" className="accent-coral" checked={effectivePayment === 'invoice'} onChange={() => setPayment('invoice')} data-id="element-914" data-mp="gds01lw6f72z" />
                      <FileText size={20} className="text-coral" data-id="element-915" data-mp="q2icjrouedg8" />
                      <span className="font-medium text-charcoal" data-id="element-916" data-mp="q4v1wpwxjq3b">Fá reikning</span>
                    </label>
                  </div>
                  {!cardAllowed && <p className="text-sm text-gray-500 -mt-3 mb-6" data-id="element-917" data-mp="zzeknwatwxk9">Fyrir fleiri en einn þátttakanda er greitt gegn reikningi.</p>}

                  {effectivePayment === 'card' && <p className="text-sm text-gray-500 mb-6" data-id="element-918" data-mp="1914ch38mhyik">Þegar þú staðfestir ferðu á örugga greiðslusíðu. Kvittun berst í tölvupósti og nýtist til að sækja um styrk hjá stéttarfélagi.</p>}

                  {effectivePayment === 'invoice' && <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6" data-id="element-919" data-mp="hxr0gydhpean">
                      <Input label="Greiðandi (fyrirtæki eða einstaklingur) *" name="company" value={invoice.company} onChange={updInv('company')} error={errors.company} data-id="element-920" data-mp="czpjcizmjp1t" />
                      <Input label="Kennitala greiðanda *" name="kennitala" value={invoice.kennitala} onChange={updInv('kennitala')} error={errors.kennitala} placeholder="000000-0000" data-id="element-921" data-mp="qwuis04u1po3" />
                      <Input label="Netfang fyrir reikning *" name="invoiceEmail" type="email" value={invoice.invoiceEmail} onChange={updInv('invoiceEmail')} error={errors.invoiceEmail} data-id="element-922" data-mp="10vjz3oh2z228" />
                      <Input label="Tilvísun / pöntunarnúmer" name="reference" value={invoice.reference} onChange={updInv('reference')} data-id="element-923" data-mp="19p6wtk4umfe2" />
                    </div>}

                  <label className="flex items-start gap-3 text-sm text-gray-600 mb-2 cursor-pointer" data-id="element-924" data-mp="1jonzdsot0d5t">
                    <input type="checkbox" className="mt-1 accent-coral" checked={agree} onChange={e => setAgree(e.target.checked)} data-id="element-925" data-mp="1eq495g36gf7n" />
                    <span data-id="element-926" data-mp="1y8qq5rkfi4se">Ég hef kynnt mér <Link to="/terms" className="text-coral hover:underline" data-id="element-927" data-mp="239ee24dd25ie">skilmála</Link> og afbókunarreglur: {CONFIG.cancellationPolicy}</span>
                  </label>
                  {errors.agree && <p className="text-sm text-red-500 mb-4" data-id="element-928" data-mp="1tbi4vi3zhkmx">{errors.agree}</p>}
                  {submitError && <p className="text-sm text-red-500 mt-4" data-id="element-929" data-mp="1fk3mndhwhngx">{submitError}</p>}

                  <div className="flex justify-between mt-8" data-id="element-930" data-mp="1fi0m3zj4gbxy">
                    <Button variant="ghost" leftIcon={<ArrowLeft size={18} data-id="element-932" data-mp="1kc4xcuzf764u" />} onClick={() => setStep(2)} data-id="element-931" data-mp="1kbvdlp4yxv7l">Til baka</Button>
                    <Button size="lg" isLoading={submitting} rightIcon={effectivePayment === 'card' ? <CreditCard size={18} data-id="element-934" data-mp="1alscf1np1ay6" /> : <Check size={18} data-id="element-935" data-mp="1ala6tef55kdl" />} onClick={submit} data-id="element-933" data-mp="15ngzgi23qkbq">
                      {effectivePayment === 'card' ? 'Staðfesta og greiða' : 'Staðfesta skráningu'}
                    </Button>
                  </div>
                </div>}

              {/* STEP 4 - confirmation (invoice) */}
              {step === 4 && selected && <div className="text-center py-6" data-id="element-936" data-mp="1c2pnun9xiuon">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-teal/10 text-teal flex items-center justify-center" data-id="element-937" data-mp="1c40sepb786gt"><Check size={32} strokeWidth={3} data-id="element-938" data-mp="1768wlk7fo07c" /></div>
                  <h3 className="text-2xl font-bold text-charcoal mb-3" data-id="element-939" data-mp="1241hwmv7y61b">Takk fyrir skráninguna</h3>
                  <p className="text-gray-600 max-w-md mx-auto leading-relaxed" data-id="element-940" data-mp="y4j5ygldl5am">
                    Skráning þín er móttekin: {selected.name}, {selected.date}. Reikningur verður sendur á {invoice.invoiceEmail} og þú færð staðfestingu og nánari upplýsingar í tölvupósti.
                  </p>
                </div>}
            </Card>
          </Animate>
        </section>

        {/* FAQ */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-941" data-mp="ee74a332sdg0">
          <Animate data-id="element-942" data-mp="addj3hh0pcqt">
            <h2 className="text-2xl font-bold text-charcoal mb-6" data-id="element-943" data-mp="aeyetaf2j1q9">Algengar spurningar</h2>
            <div className="space-y-3" data-id="element-944" data-mp="2m077gtheje74">
              {faqs.map((f, i) => <div key={i} className="border-2 border-gray-100 rounded-2xl bg-white" data-id="element-945" data-mp="2q2axcn4tilbx" data-mpi={i}>
                  <button className="w-full flex items-center justify-between gap-4 p-5 text-left font-bold text-charcoal" onClick={() => setOpenFaq(openFaq === i ? null : i)} data-id="element-946" data-mp="2v26pnp45kobn">
                    {f.q}
                    <ChevronDown size={18} className={`flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-947" data-mp="22djckgtk5596" />
                  </button>
                  {openFaq === i && <p className="px-5 pb-5 text-gray-600 leading-relaxed" data-id="element-948" data-mp="279zz56l3zbk5">{f.a}</p>}
                </div>)}
            </div>
          </Animate>
        </section>

        {/* IN-HOUSE CTA */}
        <section className="max-w-4xl mx-auto px-4 text-center" data-id="element-949" data-mp="2h3yzq15jan9z">
          <Animate data-id="element-950" data-mp="1jm8mg1vnowic">
            <Card className="p-12 bg-gradient-to-br from-coral/5 to-orange-50 border-0" data-id="element-951" data-mp="1jknqq8xlv7iw">
              <h2 className="text-2xl font-bold mb-3" data-id="element-952" data-mp="1ok0fx0by8qon">Viltu frekar fá námskeiðið til ykkar?</h2>
              <p className="text-gray-600 mb-8 max-w-xl mx-auto" data-id="element-953" data-mp="ycqqdxse0qhb">Námskeiðið er einnig í boði innanhúss, fyrir stjórnendahópa og teymi - aðlagað að aðstæðum ykkar.</p>
              <Link to="/contact" data-id="element-954" data-mp="pc117cxmtyck"><Button variant="secondary" rightIcon={<ArrowRight size={18} data-id="element-956" data-mp="pdlwj4rrtlbh" />} data-id="element-955" data-mp="pahk5k45jj9q">Hafa samband</Button></Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}